import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { EventsService } from '../../../../@shared/services/events-service';
import { CompanyService } from '../../../../@shared/services/company.service';
import { PaymentService } from '../../../../@shared/services/payment.service';
import { ToastService } from '../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FeeAcceptanceModal } from '../../../company-profile/components/company-profile-billing/components/fee-acceptance-modal/fee-acceptance-modal';
import { MyEventMembershipResponse, CompanyResponse, StripeConnectStatus } from '../../../../@shared/models/response.module';

@Component({
  selector: 'app-user-profile-my-events',
  standalone: false,
  templateUrl: './user-profile-my-events.html',
  styleUrl: './user-profile-my-events.css'
})
export class UserProfileMyEvents implements OnInit {
  memberships: MyEventMembershipResponse[] = [];
  invitedEvents: MyEventMembershipResponse[] = [];
  personalEvents: MyEventMembershipResponse[] = [];
  loading: boolean = true;
  hostingEvent: boolean = false;
  selectedTab: 'personal' | 'invited' = 'personal';

  // Payment settings
  personalCompany: CompanyResponse | null = null;
  connectStatus: StripeConnectStatus | null = null;
  loadingPayment: boolean = true;
  actionLoading: boolean = false;
  selectedBusinessType: 'individual' | 'company' = 'individual';
  customPaymentEnabled: boolean = false;
  customPaymentMethodName: string = '';
  customPaymentInstructions: string = '';
  savingCustomPayment: boolean = false;

  constructor(
    private eventsService: EventsService,
    private companyService: CompanyService,
    private paymentService: PaymentService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private modalService: NgbModal,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadMyEvents();
    this.loadPaymentSettings();
  }

  loadMyEvents() {
    this.loading = true;
    this.eventsService.getMyMemberEvents().subscribe({
      next: (data) => {
        this.memberships = data || [];
        this.splitAndSort();
        if (this.personalEvents.length === 0 && this.invitedEvents.length > 0) {
          this.selectedTab = 'invited';
        }
        this.loading = false;
      },
      error: () => {
        this.memberships = [];
        this.invitedEvents = [];
        this.personalEvents = [];
        this.loading = false;
      }
    });
  }

  private splitAndSort() {
    this.invitedEvents = this.sortByUpcoming(
      this.memberships.filter(m => m.member_id !== null)
    );
    this.personalEvents = this.sortByUpcoming(
      this.memberships.filter(m => m.member_id === null)
    );
  }

  private sortByUpcoming(items: MyEventMembershipResponse[]): MyEventMembershipResponse[] {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return items.sort((a, b) => {
      const dateA = a.event.start_date ? new Date(a.event.start_date) : null;
      const dateB = b.event.start_date ? new Date(b.event.start_date) : null;
      const upcomingA = dateA && dateA >= now;
      const upcomingB = dateB && dateB >= now;
      if (upcomingA && !upcomingB) return -1;
      if (!upcomingA && upcomingB) return 1;
      if (dateA && dateB) return dateA.getTime() - dateB.getTime();
      if (dateA) return -1;
      if (dateB) return 1;
      return 0;
    });
  }

  hostEvent() {
    this.hostingEvent = true;
    this.companyService.getOrCreatePersonalCompany().subscribe({
      next: (company) => {
        this.hostingEvent = false;
        if (!company?.company_id) {
          console.error('Personal company missing company_id:', company);
          this.toastService.error(this.translateService.instant('userProfile.myEvents.hostEventError'));
          return;
        }
        console.log('Navigating to create event with personal company:', company.company_id, company.company_name, 'is_personal:', company.is_personal);
        this.router.navigate(['/company-profile/create-event', company.company_id]);
      },
      error: (err) => {
        console.error('Failed to get/create personal company:', err);
        this.hostingEvent = false;
        this.toastService.error(this.translateService.instant('userProfile.myEvents.hostEventError'));
      }
    });
  }

  // ==================== Payment Settings ====================

  loadPaymentSettings() {
    this.loadingPayment = true;
    this.companyService.getOrCreatePersonalCompany().subscribe({
      next: (company) => {
        this.personalCompany = company;
        this.customPaymentEnabled = company.custom_payment_enabled || false;
        this.customPaymentMethodName = company.custom_payment_method_name || '';
        this.customPaymentInstructions = company.custom_payment_instructions || '';
        this.loadStripeStatus();
      },
      error: () => {
        this.loadingPayment = false;
      }
    });
  }

  private loadStripeStatus() {
    if (!this.personalCompany) return;
    this.paymentService.getConnectStatus(this.personalCompany.company_id).subscribe({
      next: (status) => {
        this.connectStatus = status;
        this.loadingPayment = false;
      },
      error: (err) => {
        if (err.status === 404) {
          this.connectStatus = null;
        }
        this.loadingPayment = false;
      }
    });
  }

  isStripeConnected(): boolean {
    return this.connectStatus?.status === 'active' && this.connectStatus?.charges_enabled === true;
  }

  startOnboarding() {
    if (!this.personalCompany) return;
    const alreadyAccepted = this.connectStatus?.platform_fee_accepted_at;

    if (!alreadyAccepted) {
      const modalRef = this.modalService.open(FeeAcceptanceModal, { size: 'lg', centered: true });
      modalRef.componentInstance.companyName = this.personalCompany.company_name;
      modalRef.result.then((accepted) => {
        if (accepted) {
          this.proceedWithOnboarding(true);
        }
      }).catch(() => {});
    } else {
      this.proceedWithOnboarding(false);
    }
  }

  private proceedWithOnboarding(acceptFeeTerms: boolean) {
    if (!this.personalCompany) return;
    this.actionLoading = true;

    this.paymentService.createConnectAccount(this.personalCompany.company_id, this.selectedBusinessType, acceptFeeTerms).subscribe({
      next: (response) => {
        window.location.href = response.onboarding_url;
      },
      error: () => {
        this.toastService.error(this.translateService.instant('userProfile.myEvents.payment.onboardingError'));
        this.actionLoading = false;
      }
    });
  }

  continueOnboarding() {
    if (!this.personalCompany) return;
    this.actionLoading = true;

    this.paymentService.refreshOnboarding(this.personalCompany.company_id).subscribe({
      next: (response) => {
        window.location.href = response.onboarding_url;
      },
      error: () => {
        this.toastService.error(this.translateService.instant('userProfile.myEvents.payment.onboardingError'));
        this.actionLoading = false;
      }
    });
  }

  openDashboard() {
    if (!this.personalCompany) return;
    this.actionLoading = true;

    this.paymentService.getDashboardLink(this.personalCompany.company_id).subscribe({
      next: (response) => {
        window.open(response.dashboard_url, '_blank');
        this.actionLoading = false;
      },
      error: () => {
        this.toastService.error(this.translateService.instant('userProfile.myEvents.payment.dashboardError'));
        this.actionLoading = false;
      }
    });
  }

  saveCustomPaymentSettings() {
    if (!this.personalCompany) return;
    this.savingCustomPayment = true;

    this.companyService.editCompany({
      company_id: this.personalCompany.company_id,
      custom_payment_enabled: this.customPaymentEnabled,
      custom_payment_method_name: this.customPaymentMethodName,
      custom_payment_instructions: this.customPaymentInstructions
    } as any).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('userProfile.myEvents.payment.saved'));
        this.savingCustomPayment = false;
      },
      error: () => {
        this.toastService.error(this.translateService.instant('userProfile.myEvents.payment.saveError'));
        this.savingCustomPayment = false;
      }
    });
  }

  getStatusBadgeClass(): string {
    if (!this.connectStatus) return 'badge-light-secondary';
    switch (this.connectStatus.status) {
      case 'active': return 'badge-light-success';
      case 'onboarding':
      case 'pending': return 'badge-light-warning';
      case 'restricted': return 'badge-light-danger';
      default: return 'badge-light-secondary';
    }
  }

  getStatusLabel(): string {
    if (!this.connectStatus) return this.translateService.instant('userProfile.myEvents.payment.statusNotConnected');
    switch (this.connectStatus.status) {
      case 'active': return this.translateService.instant('userProfile.myEvents.payment.statusConnected');
      case 'onboarding': return this.translateService.instant('userProfile.myEvents.payment.statusOnboarding');
      case 'pending': return this.translateService.instant('userProfile.myEvents.payment.statusPending');
      case 'restricted': return this.translateService.instant('userProfile.myEvents.payment.statusRestricted');
      default: return this.translateService.instant('userProfile.myEvents.payment.statusNotConnected');
    }
  }
}
